import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

import { useAnalytics } from "../../providers/AnalyticsProvider";
import type { AuthUser } from "../../stores/auth";
import { useAuthStore } from "../../stores/auth";
import { useShallow } from "zustand/shallow";
import { API_ORIGIN, apiAxios } from "@/api/config";
import { MarkeerooBrand } from "@/components/MarkeerooBrand";
import { useNotification } from "@/providers/NotificationProvider";
import { inputClass, mutedText } from "@/styles/variants";
import { ui, notifications } from "@/strings";
import { getErrorCode, getErrorMessage, AUTH_ERROR_CODES } from "@/api/errors";
import { sanitizeRedirect } from "@/lib/security/redirects";
import { isAxiosError } from "axios";
import { buttonVariants } from "@markeroo/theme";

interface AuthResponse {
  user: AuthUser;
  access_token: string;
}

const logoUrl = new URL("../../images/logo-full.png", import.meta.url).href;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const analytics = useAnalytics();
  const { show } = useNotification();
  const { user, hydrated, setUser } = useAuthStore(useShallow((state) => ({
    user: state.user,
    hydrated: state.hydrated,
    setUser: state.setUser,
  })));
  const [isGoogleRedirecting, setGoogleRedirecting] = useState(false);

  // Pre-fill email from navigation state (when redirected from register)
  const prefillEmail = (location.state as { email?: string } | null)?.email || "";
  const [showEmailForm, setShowEmailForm] = useState(!!prefillEmail);
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordLabelRef = useRef<HTMLLabelElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);
  const [forgotPasswordPosition, setForgotPasswordPosition] = useState<{ top: number; right: number } | null>(null);

  // Track mount status for async operations
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (showEmailForm && passwordLabelRef.current) {
      const label = passwordLabelRef.current;
      const container = label.closest(".rounded-2xl");
      if (container) {
        const labelRect = label.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        setForgotPasswordPosition({
          top: labelRect.top - containerRect.top,
          right: 32, // matches p-8 padding
        });
      }
    }
  }, [showEmailForm]);

  // Focus email input when form is revealed (accessibility)
  useEffect(() => {
    if (showEmailForm && emailInputRef.current) {
      // Small delay to ensure DOM is ready after transition
      const timeoutId = setTimeout(() => {
        emailInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [showEmailForm]);

  const nextPath = useMemo(() => {
    if (typeof window === "undefined") {
      return "/";
    }
    const params = new URLSearchParams(window.location.search ?? "");
    // When extension=1 is present, allow redirects to the API origin
    // for the extension OAuth callback flow
    const isExtensionFlow = params.get("extension") === "1";
    return sanitizeRedirect(params.get("next"), { allowApiOrigin: isExtensionFlow });
  }, []);

  const [sessionValidated, setSessionValidated] = useState(false);

  // Reset sessionValidated when user logs out (null) to ensure re-validation on next login
  useEffect(() => {
    if (!user) {
      setSessionValidated(false);
    }
  }, [user]);

  // Validate session before redirecting (prevents race condition)
  useEffect(() => {
    if (!hydrated || !user || sessionValidated) {
      return;
    }

    let cancelled = false;

    // Validate the session is still active before redirecting
    void (async () => {
      try {
        await apiAxios.get("/auth/me");
        // Only update state if component is still mounted
        if (!cancelled) {
          // Session is valid, mark as validated and allow redirect
          setSessionValidated(true);
        }
      } catch (err) {
        // Only update state if component is still mounted
        if (!cancelled) {
          // Use type guard for proper type safety
          if (isAxiosError(err)) {
            // Check if this is a network error (API unreachable) vs auth error
            if (!err.response) {
              // Network error - API is unreachable
              show(notifications.generic.networkError, "error");
            } else if (err.response.status === 401 || err.response.status === 403) {
              // Auth error - session expired/invalid, silently clear
            } else {
              // Other error - notify user
              show(notifications.generic.loadError, "error");
            }
          } else {
            // Unexpected non-Axios error
            show(notifications.generic.loadError, "error");
          }

          // Clear stale auth state regardless of error type
          setUser(null);
          setSessionValidated(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hydrated, user, sessionValidated, setUser, show]);

  useEffect(() => {
    if (hydrated && user && sessionValidated) {
      // Session is validated, safe to redirect
      if (nextPath.startsWith("http://") || nextPath.startsWith("https://")) {
        window.location.href = nextPath;
      } else {
        void navigate(nextPath, { replace: true });
      }
    }
  }, [hydrated, user, sessionValidated, navigate, nextPath]);

  // Check for OAuth error on mount
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const params = new URLSearchParams(window.location.search ?? "");
    const oauthError = params.get("oauth_error");
    if (oauthError) {
      // Show user-friendly message based on error type
      if (oauthError === "access_denied") {
        show(notifications.auth.oauthDenied, "error");
      } else {
        show(notifications.auth.oauthFailed, "error");
      }
      // Clean up URL to remove the error parameter
      params.delete("oauth_error");
      const newSearch = params.toString();
      const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [show]);

  const handleGoogleSignIn = () => {
    setGoogleRedirecting(true);
    analytics.track("google_oauth_start");
    const target = `${API_ORIGIN}/api/auth/google?next=${encodeURIComponent(nextPath)}`;
    window.location.assign(target);
  };

  const handleEmailLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    apiAxios
      .post<AuthResponse>("/auth/login", { email, password })
      .then((response) => {
        // Check if component is still mounted before state updates
        if (!isMountedRef.current) return;

        const { user: loggedInUser } = response.data;
        analytics.track("login_email_success");
        setUser(loggedInUser);

        // For absolute URLs (like extension callback), use window.location
        // For relative paths, use React Router navigate
        if (nextPath.startsWith("http://") || nextPath.startsWith("https://")) {
          window.location.href = nextPath;
        } else {
          void navigate(nextPath, { replace: true });
        }
      })
      .catch((err: unknown) => {
        // Check if component is still mounted before state updates
        if (!isMountedRef.current) return;

        analytics.track("login_email_error");

        // Check for USER_NOT_FOUND error code - redirect to register
        const errorCode = getErrorCode(err);
        if (errorCode === AUTH_ERROR_CODES.USER_NOT_FOUND) {
          show(notifications.auth.accountNotFound, "info");
          void navigate("/register", { state: { email } });
          return;
        }

        const errorMessage = getErrorMessage(err);
        if (errorMessage?.includes("verify your email")) {
          show(notifications.auth.emailUnverified, "error");
        } else {
          show(errorMessage || notifications.auth.loginFailed, "error");
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsSubmitting(false);
        }
      });
  };


  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 py-10">
      <div className="relative flex w-full max-w-md flex-col gap-6 rounded-2xl bg-white p-8 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <div className="flex flex-col items-center gap-2">
          <Link to="/">
            <img src={logoUrl} alt="Markeroo logo" className="h-24 w-auto" />
          </Link>
          <h1 className="text-h3">
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <MarkeerooBrand />
            </Link>
          </h1>
          <h2 className="text-lg font-semibold text-text-theme-primary text-center">
            {ui.labels.signInToYourAccount}
          </h2>
        </div>

        {/* Google Sign In - Primary */}
        <button
          type="button"
          className={buttonVariants.link}
          onClick={handleGoogleSignIn}
          disabled={isGoogleRedirecting || isSubmitting}
        >
          <GoogleIcon />
          <span>
            {isGoogleRedirecting ? ui.actions.redirecting : ui.actions.signInWithGoogle}
          </span>
        </button>

        {!showEmailForm ? (
          <>
            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-text-theme-muted">{ui.labels.orContinueWith}</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* Show email form button */}
            <button
              type="button"
              className={`${buttonVariants.secondary} w-full`}
              onClick={() => setShowEmailForm(true)}
              disabled={isGoogleRedirecting}
            >
              {ui.actions.signInWithEmail}
            </button>
          </>
        ) : (
          <>
            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-text-theme-muted">{ui.labels.orContinueWith}</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleEmailLogin} className="flex flex-col gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-theme-secondary">
                  {ui.labels.email}
                </label>
                <input
                  ref={emailInputRef}
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${inputClass} mt-1`}
                  required
                  disabled={isSubmitting}
                  autoComplete="email"
                />
              </div>

              <div className="relative">
                <label ref={passwordLabelRef} htmlFor="password" className="block text-sm font-medium text-text-theme-secondary">
                  {ui.labels.password}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} mt-1`}
                  required
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                className={`${buttonVariants.primary} w-full`}
                disabled={isSubmitting || !email || !password}
              >
                {isSubmitting ? ui.actions.loggingIn : ui.actions.logIn}
              </button>
            </form>
          </>
        )}

        {/* Footer link */}
        <p className={`${mutedText} text-center text-sm`}>
          {ui.labels.dontHaveAccount}{" "}
          <Link to="/register" className="font-medium text-brand-600 hover:text-brand-700">
            {ui.actions.createAccount}
          </Link>
        </p>

        {/* Forgot password - last in DOM for tab order, positioned visually next to password label */}
        {showEmailForm && forgotPasswordPosition && (
          <Link
            to="/forgot-password"
            state={{ email }}
            className="absolute text-xs font-medium text-brand-600 hover:text-brand-700"
            style={{ top: forgotPasswordPosition.top, right: forgotPasswordPosition.right }}
          >
            {ui.actions.forgotPassword}
          </Link>
        )}
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <span aria-hidden="true" className="mr-2 text-text-theme-muted">
      {/* eslint-disable-next-line no-restricted-syntax -- Google brand logo */}
      <svg className="h-5 w-5" viewBox="0 0 18 18" focusable="false">
        <path
          d="M17.64 9.2045c0-.638-.057-1.252-.163-1.841H9v3.481h4.844c-.209 1.125-.842 2.078-1.795 2.716v2.258h2.908c1.704-1.568 2.683-3.877 2.683-6.614z"
          fill="#4285F4" // eslint-disable-line no-restricted-syntax -- Google brand colors
        />
        <path
          d="M9 18c2.43 0 4.468-.806 5.957-2.183l-2.908-2.258c-.806.54-1.838.859-3.049.859-2.348 0-4.335-1.584-5.042-3.708H.957v2.327C2.438 15.978 5.481 18 9 18z"
          fill="#34A853" // eslint-disable-line no-restricted-syntax -- Google brand colors
        />
        <path
          d="M3.958 10.71a5.41 5.41 0 01-.282-1.71c0-.594.102-1.17.282-1.71V4.963H.957A9 9 0 000 9c0 1.467.352 2.853.957 4.037l3.001-2.327z"
          fill="#FBBC05" // eslint-disable-line no-restricted-syntax -- Google brand colors
        />
        <path
          d="M9 3.563c1.32 0 2.5.454 3.438 1.347l2.579-2.578C13.463.835 11.425 0 9 0 5.481 0 2.438 2.022.957 4.963l3.001 2.327C4.665 5.166 6.652 3.563 9 3.563z"
          fill="#EA4335" // eslint-disable-line no-restricted-syntax -- Google brand colors
        />
      </svg>
    </span>
  );
}

export const DevLoginPage = LoginPage;
